import { openWebview } from '@docs/common'
import Button from '@docs/common/components/Button'
import { Spin } from '@docs/common'
import { getMallDomain } from 'common/util'
import { useRequest } from 'ahooks'
import classnames from 'classnames/bind'
import { TFunction } from '@volcengine/i18n'
import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { CouponItemType } from 'servers/coupon'
import { CouponVariant, OriginRule, getCouponByRuleId } from 'servers/recommend'
import styles from './styles.module.scss'

const cx = classnames.bind(styles)

export type PopCoupon = CouponItemType & { sku: string; handle: string }

type Props = {
  extra?: ReactNode
  variant?: CouponVariant
  recommendTrans: TFunction
  loading: boolean
  onVariantClickTrack?: (variant: CouponVariant) => void
  onGetCouponSucc?: (popCoupon: PopCoupon) => void
  formatPrice?: (price: number) => string
}
// 判断variant是否可用
export const isVariantEnable = (variant?: CouponVariant) => {
  return !/9{4,}\./.test(variant?.price || '') && variant?.product.status === 'ACTIVE'
}
export const processShopifyImg = (url?: string, maxWidth: number = 320) => {
  if (!url) {
    return ''
  }
  const urlSplit = url.split('.')
  const suffix = urlSplit.slice(-1)[0]
  const prefix = `${urlSplit.slice(0, -1).join('.')}_${maxWidth}x`
  return [prefix, suffix].join('.')
}

const VariantItem: React.FC<Props> = ({
  onVariantClickTrack,
  extra,
  onGetCouponSucc,
  loading = false,
  recommendTrans,
  variant,
  formatPrice,
}) => {
  const [receivedCoupon, setReceivedCoupon] = useState<CouponItemType>()

  const sku = useMemo(() => variant?.sku!, [variant?.sku])

  // 获取优惠券
  const {
    data: couponData,
    run: runGetCoupon,
    loading: getCouponLoading,
  } = useRequest(
    () => {
      return getCouponByRuleId({
        sku,
        rule_id: variant?.originRule?.rule_id!,
        scene: 'recommend_solution',
        receive_type: 2,
      })
    },
    {
      manual: true,
      onSuccess(res) {
        setReceivedCoupon(res.coupon)
        onGetCouponSucc?.({
          ...res.coupon,
          sku,
          handle: variant?.product.handle!,
        })
      },
    }
  )
  console.log('couponData',couponData);
  // 组装折扣信息展示
  const getCouponDiscount = useCallback(
    (rule?: OriginRule | CouponItemType) => {
      if (!rule) {
        return ''
      }
      const _value = Math.abs(Number(rule.value))
      return rule.value_type === 'percentage' ? _value + '%' : formatPrice?.(_value)
    },
    [formatPrice]
  )

  // 商品图片
  const imgUrl = useMemo(() => {
    return processShopifyImg(variant?.image?.url, 140)
  }, [variant?.image?.url])

  // 计算折扣
  const discount = useMemo(() => {
    return getCouponDiscount(variant?.originRule)
  }, [getCouponDiscount, variant?.originRule])

  // 计算已领取的优惠券折扣
  const receivedCouponDiscount = useMemo(() => {
    return getCouponDiscount(receivedCoupon || variant?.coupon)
  }, [getCouponDiscount, receivedCoupon, variant?.coupon])

  // 是否售罄
  const isSoldOut = useMemo(() => {
    return variant?.inventoryQuantity! <= 0
  }, [variant?.inventoryQuantity])

  // 是否禁用
  const disabled = useMemo(() => {
    return isSoldOut || !isVariantEnable(variant)
  }, [isSoldOut, variant])

  // 打开商品详情页
  const openListing = async () => {
    if (disabled) {
      return
    }
    onVariantClickTrack?.(variant!)
    openWebview(`${await getMallDomain()}/products/variant/${variant?.product.handle}/${sku}`)
  }

  // 领取优惠券
  const openReceivedCoupon = () => {
    onGetCouponSucc?.({
      ...(receivedCoupon || variant!.coupon),
      sku,
      handle: variant?.product.handle!,
    })
  }

  // 未加载完成或者没有variant
  if (!loading && !variant) {
    return null
  }
  return (
    <Spin spinning={loading || getCouponLoading}>
      <div
        className={cx({
          variantItem: true,
          disabled,
          'variant-item': true,
        })}
        data-key={sku}
        onClick={openListing}
      >
        {loading ? null : (
          <>
            <div className={styles.imgWrap}>{imgUrl && <img src={imgUrl} alt={variant?.product?.title} />}</div>
            <div className={styles.info}>
              <div className={styles.title}>{variant?.product?.title}</div>
              <div className={styles.subtitle}>{variant?.product?.subtitle?.value}</div>
              <div className={styles.buyInfo}>
                {!disabled ? (
                  <>
                    {discount && !receivedCoupon ? (
                      <div
                        className={styles.couponInfo}
                        onClick={e => {
                          e.stopPropagation()
                          runGetCoupon()
                        }}
                      >
                        {recommendTrans('rec_coupon_badge_txt', { discount })}
                      </div>
                    ) : receivedCouponDiscount ? (
                      <div
                        className={styles.couponInfo}
                        onClick={e => {
                          e.stopPropagation()
                          openReceivedCoupon()
                        }}
                      >
                        {recommendTrans('rec_coupon_badge_txt', { discount: receivedCouponDiscount })}
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </>
                ) : (
                  <div></div>
                )}
                <Button
                  disabled={disabled}
                  className={cx(styles.btn, {
                    enterBtn: !isSoldOut,
                  })}
                  type="primary"
                >
                  {isSoldOut ? recommendTrans('common_sold_out') : null}
                </Button>
              </div>
              {extra}
            </div>
          </>
        )}
      </div>
    </Spin>
  )
}

export default VariantItem
